import { lazy } from 'react';

import { LayoutType, PageType } from '@shared/constants/constants';
import {
  MEDREFILL_ORDER_FOR_OTHERS_PATH,
  MEDREFILL_ORDER_TYPE_PATH,
  MEDREFILL_PATH,
  MEDREFILL_POLYCLINICS_PATH,
  MEDREFILL_REFILL_ADD_PARTICULARS_PATH,
  MEDREFILL_REFILL_COLLECTIONS_PATH,
  MEDREFILL_REFILL_ORIGINAL_DETAILS_PATH,
  MEDREFILL_REFILL_PAYMENT_MODE_PATH,
  MEDREFILL_REFILL_PRESCRIPTION_LIST_PATH,
  MEDREFILL_REFILL_REFILL_OPTIONS_PATH,
  MEDREFILL_REFILL_SUCCESSFUL_REQUEST_PATH,
  MEDREFILL_REFILL_SUMMARY_PATH,
  MEDREFILL_REFILL_TNC_PATH,
  MEDREFILL_REFILL_UNSUCCESSFUL_REQUEST_PATH,
  MEDREFILL_SUCCESSFUL_REQUEST_PATH,
  MEDREFILL_TOPUP_ADD_PARTICULARS_PATH,
  MEDREFILL_TOPUP_CLINICS_PATH,
  MEDREFILL_TOPUP_COLLECTIONS_PATH,
  MEDREFILL_TOPUP_PAYMENT_MODE_PATH,
  MEDREFILL_TOPUP_REPEAT_PRESCRIPTION_LIST_PATH,
  MEDREFILL_TOPUP_SUCCESSFUL_REQUEST_PATH,
  MEDREFILL_TOPUP_SUMMARY_PATH,
  MEDREFILL_TOPUP_TNC_PATH,
  MEDREFILL_TOPUP_UNSUCCESSFUL_REQUEST_PATH,
  MEDREFILL_UNSUCCESSFUL_REQUEST_PATH,
} from '@shared/constants/paths';
import { SERVICE_CATALOG_CODE } from '@shared/constants/serviceCatalog';
import type { RouteConfig } from '@shared/types/common.types';

import { MEDREFILL_OTHERS_PATH } from './constants';

const SelectInstitution = lazy(() => import('./pages/SelectInstitution/SelectInstitution'));
const SelectPolyclinic = lazy(() => import('./pages/SelectPolyclinic/SelectPolyclinic'));
const SelectMedicationOrderType = lazy(() => import('./pages/SelectMedicationOrderType/SelectMedicationOrderType'));
const OriginalDetails = lazy(() => import('./pages/OriginalDetails/OriginalDetails'));
const RefillOptions = lazy(() => import('./pages/RefillOptions/RefillOptions'));
const SelectPaymentMode = lazy(() => import('./pages/SelectPaymentMode/SelectPaymentMode'));
const AddParticulars = lazy(() => import('./pages/AddParticulars/AddParticulars'));
const MedRefillSummary = lazy(() => import('./pages/MedRefillSummary/MedRefillSummary'));
const Collections = lazy(() => import('./pages/Collections/Collections'));
const Clinic = lazy(() => import('./pages/Clinic/Clinic'));
const SuccessfulMedRequest = lazy(() => import('./pages/SuccessfulMedRequest/SuccessfulMedRequest'));
const UnsuccessfulMedRequest = lazy(() => import('./pages/UnsuccessfulMedRequest/UnsuccessfulMedRequest'));
const Prescriptions = lazy(() => import('./pages/Prescriptions/Prescriptions'));
const RepeatPrescriptions = lazy(() => import('./pages/RepeatPrescriptions/RepeatPrescriptions'));
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions/TermsAndConditions'));

export const MEDREFILL_OTHERS_ROUTE: RouteConfig[] = [
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    backUrl: MEDREFILL_ORDER_FOR_OTHERS_PATH,
    component: () => <SelectInstitution isOrderForSomeone isSingpass />,
    displayTitle: () => 'Medication Refill',
    pageType: PageType.PRIVATE,
    path: `${MEDREFILL_PATH}${MEDREFILL_OTHERS_PATH}`,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <SelectPolyclinic isOrderForSomeone isSingpass />,
    displayTitle: () => 'Medication Refill',
    pageType: PageType.PRIVATE,
    path: `${MEDREFILL_POLYCLINICS_PATH}${MEDREFILL_OTHERS_PATH}`,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <SelectMedicationOrderType isOrderForSomeone isSingpass />,
    displayTitle: () => 'Medication Refill',
    pageType: PageType.PRIVATE,
    path: `${MEDREFILL_ORDER_TYPE_PATH}${MEDREFILL_OTHERS_PATH}`,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <OriginalDetails isOrderForSomeone isSingpass />,
    displayTitle: () => 'Medication Refill',
    pageType: PageType.PRIVATE,
    path: `${MEDREFILL_REFILL_ORIGINAL_DETAILS_PATH}${MEDREFILL_OTHERS_PATH}`,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <RefillOptions isOrderForSomeone isSingpass />,
    displayTitle: () => 'Medication Refill',
    pageType: PageType.PRIVATE,
    path: `${MEDREFILL_REFILL_REFILL_OPTIONS_PATH}${MEDREFILL_OTHERS_PATH}`,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <Prescriptions isOrderForSomeone isSingpass />,
    displayTitle: () => 'Medication Refill',
    pageType: PageType.PRIVATE,
    path: `${MEDREFILL_REFILL_PRESCRIPTION_LIST_PATH}${MEDREFILL_OTHERS_PATH}`,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <SelectPaymentMode isOrderForSomeone isSingpass />,
    displayTitle: () => 'Medication Refill',
    pageType: PageType.PRIVATE,
    path: `${MEDREFILL_REFILL_PAYMENT_MODE_PATH}${MEDREFILL_OTHERS_PATH}`,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <SelectPaymentMode isOrderForSomeone isSingpass isTopup />,
    displayTitle: () => 'Repeat Prescription',
    pageType: PageType.PRIVATE,
    path: `${MEDREFILL_TOPUP_PAYMENT_MODE_PATH}${MEDREFILL_OTHERS_PATH}`,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <Collections isOrderForSomeone isSingpass />,
    displayTitle: () => 'Medication Refill',
    pageType: PageType.PRIVATE,
    path: `${MEDREFILL_REFILL_COLLECTIONS_PATH}${MEDREFILL_OTHERS_PATH}`,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <Collections isOrderForSomeone isSingpass isTopup />,
    displayTitle: () => 'Repeat Prescription',
    pageType: PageType.PRIVATE,
    path: `${MEDREFILL_TOPUP_COLLECTIONS_PATH}${MEDREFILL_OTHERS_PATH}`,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <AddParticulars isOrderForSomeone isSingpass isTopup />,
    displayTitle: () => 'Repeat Prescription',
    pageType: PageType.PRIVATE,
    path: `${MEDREFILL_TOPUP_ADD_PARTICULARS_PATH}${MEDREFILL_OTHERS_PATH}`,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <AddParticulars isOrderForSomeone isSingpass />,
    displayTitle: () => 'Medication Refill',
    pageType: PageType.PRIVATE,
    path: `${MEDREFILL_REFILL_ADD_PARTICULARS_PATH}${MEDREFILL_OTHERS_PATH}`,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <MedRefillSummary isOrderForSomeone isSingpass />,
    displayTitle: () => 'Medication Refill',
    pageType: PageType.PRIVATE,
    path: `${MEDREFILL_REFILL_SUMMARY_PATH}${MEDREFILL_OTHERS_PATH}`,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <MedRefillSummary isOrderForSomeone isSingpass isTopup />,
    displayTitle: () => 'Repeat Prescription',
    pageType: PageType.PRIVATE,
    path: `${MEDREFILL_TOPUP_SUMMARY_PATH}${MEDREFILL_OTHERS_PATH}`,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    backUrl: `${MEDREFILL_PATH}${MEDREFILL_OTHERS_PATH}`,
    component: () => <SuccessfulMedRequest isOrderForSomeone isSingpass />,
    displayTitle: () => 'Medication Refill',
    pageType: PageType.PRIVATE,
    path: `${MEDREFILL_SUCCESSFUL_REQUEST_PATH}${MEDREFILL_OTHERS_PATH}`,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    backUrl: `${MEDREFILL_PATH}${MEDREFILL_OTHERS_PATH}`,
    component: () => <SuccessfulMedRequest isOrderForSomeone isSingpass isTopup />,
    displayTitle: () => 'Repeat Prescription',
    pageType: PageType.PRIVATE,
    path: `${MEDREFILL_TOPUP_SUCCESSFUL_REQUEST_PATH}${MEDREFILL_OTHERS_PATH}`,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    backUrl: `${MEDREFILL_PATH}${MEDREFILL_OTHERS_PATH}`,
    component: () => <SuccessfulMedRequest isOrderForSomeone isSingpass />,
    displayTitle: () => 'Medication Refill',
    pageType: PageType.PRIVATE,
    path: `${MEDREFILL_REFILL_SUCCESSFUL_REQUEST_PATH}${MEDREFILL_OTHERS_PATH}`,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    backUrl: `${MEDREFILL_PATH}${MEDREFILL_OTHERS_PATH}`,
    component: () => <UnsuccessfulMedRequest isOrderForSomeone isSingpass />,
    displayTitle: () => 'Medication Refill',
    pageType: PageType.PRIVATE,
    path: `${MEDREFILL_UNSUCCESSFUL_REQUEST_PATH}${MEDREFILL_OTHERS_PATH}`,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    backUrl: `${MEDREFILL_PATH}${MEDREFILL_OTHERS_PATH}`,
    component: () => <UnsuccessfulMedRequest isOrderForSomeone isSingpass />,
    displayTitle: () => 'Medication Refill',
    pageType: PageType.PRIVATE,
    path: `${MEDREFILL_REFILL_UNSUCCESSFUL_REQUEST_PATH}${MEDREFILL_OTHERS_PATH}`,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    backUrl: `${MEDREFILL_PATH}${MEDREFILL_OTHERS_PATH}`,
    component: () => <UnsuccessfulMedRequest isOrderForSomeone isSingpass isTopup />,
    displayTitle: () => 'Repeat Prescription',
    pageType: PageType.PRIVATE,
    path: `${MEDREFILL_TOPUP_UNSUCCESSFUL_REQUEST_PATH}${MEDREFILL_OTHERS_PATH}`,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <Clinic isOrderForSomeone isSingpass isTopup />,
    displayTitle: () => 'Repeat Prescription',
    pageType: PageType.PRIVATE,
    path: `${MEDREFILL_TOPUP_CLINICS_PATH}${MEDREFILL_OTHERS_PATH}`,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <RepeatPrescriptions isOrderForSomeone isSingpass isTopup />,
    displayTitle: () => 'Repeat Prescription',
    pageType: PageType.PRIVATE,
    path: `${MEDREFILL_TOPUP_REPEAT_PRESCRIPTION_LIST_PATH}${MEDREFILL_OTHERS_PATH}`,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <TermsAndConditions isOrderForSomeone isSingpass />,
    displayTitle: () => 'Medication Refill',
    pageType: PageType.PRIVATE,
    path: `${MEDREFILL_REFILL_TNC_PATH}${MEDREFILL_OTHERS_PATH}`,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE, LayoutType.SHARED],
    component: () => <TermsAndConditions isOrderForSomeone isSingpass isTopup />,
    displayTitle: () => 'Repeat Prescription',
    pageType: PageType.PRIVATE,
    path: `${MEDREFILL_TOPUP_TNC_PATH}${MEDREFILL_OTHERS_PATH}`,
    serviceCatalogCode: SERVICE_CATALOG_CODE.medicationRefill,
  },
];
